import React, { useCallback } from "react";
import { Avatar, Box, Card, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { formatDate } from "../plans/formatDate";
import { PRIORITY_COLORS, STATUS_COLORS, colorForName, initialsFor, formatBudget, labelForStatus } from "./briefUiUtils";

const BriefCard = React.memo(function BriefCard({ brief, onView, onEdit, onDelete, currentUserId, isSuperUser }) {
  const { title, brand, description, deliverableType, deadline, priority, budget, status, assignee, category, createdBy } = brief;
  const statusStyle = STATUS_COLORS[status] || STATUS_COLORS.Pending;
  const priorityColor = PRIORITY_COLORS[priority] || PRIORITY_COLORS.Medium;
  const assignees = assignee || [];
  const assigneeNames = assignees.map((u) => u.name);
  const categories = category || [];
  // Mirrors the backend's DELETE check (routes/briefs.js) — creator, or a super
  // user acting on any brief regardless of who created it.
  const canDelete = isSuperUser || (Boolean(currentUserId) && createdBy?.id === currentUserId);

  const handleCardClick = useCallback(() => onView(brief), [onView, brief]);
  // Both icons stop propagation — without it, clicking either would also
  // bubble up to the card's own onClick and open the detail view right
  // behind the edit modal / delete confirm it just opened.
  const handleEditClick = useCallback((e) => {
    e.stopPropagation();
    onEdit(brief);
  }, [onEdit, brief]);
  const handleDeleteClick = useCallback((e) => {
    e.stopPropagation();
    onDelete(brief);
  }, [onDelete, brief]);

  return (
    <Card
      onClick={handleCardClick}
      elevation={0}
      sx={{
        borderRadius: "18px",
        border: "1px solid #ECEDF0",
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
        cursor: "pointer",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 12px 28px rgba(16,24,40,0.10)",
          borderColor: "#F3D9CC",
        },
      }}
    >
      {/* Priority accent bar */}
      <Box sx={{ height: 4, background: priorityColor, flexShrink: 0 }} />

      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "11px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, rgba(255,82,0,0.12), rgba(255,140,0,0.07))",
                border: "1px solid rgba(255,82,0,0.16)",
              }}
            >
              <AssignmentOutlinedIcon sx={{ color: "#FF5200", fontSize: 19 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Tooltip title={title}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 14.5,
                    color: "#111827",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.3,
                  }}
                >
                  {title}
                </Typography>
              </Tooltip>
              <Typography sx={{ fontSize: 12.5, color: "#9CA3AF", mt: 0.2 }}>{brand?.name || "No brand"}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={handleEditClick}
                sx={{
                  color: "#9CA3AF",
                  background: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  "&:hover": { background: "#FFF4EE", color: "#FF5200", borderColor: "#FFD6BE" },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={handleDeleteClick}
                  sx={{
                    color: "#9CA3AF",
                    background: "#F9FAFB",
                    border: "1px solid #E5E7EB",
                    "&:hover": { background: "#FEF2F2", color: "#EF4444", borderColor: "#FECACA" },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Description */}
        <Typography
          sx={{
            mt: 1.75,
            fontSize: 13,
            lineHeight: 1.5,
            color: description ? "#4B5563" : "#C1C5CC",
            fontStyle: description ? "normal" : "italic",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            minHeight: 39,
          }}
        >
          {description || "No description added"}
        </Typography>

        {/* Tags row */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
          {deliverableType && (
            <Chip
              label={deliverableType}
              size="small"
              sx={{ bgcolor: "#F3F4F6", color: "#4B5563", fontWeight: 600, fontSize: 11, height: 22 }}
            />
          )}
          {categories.map((c) => (
            <Chip
              key={c.id}
              label={c.name}
              size="small"
              sx={{ bgcolor: "#EEF2FF", color: "#4338CA", fontWeight: 600, fontSize: 11, height: 22 }}
            />
          ))}
          <Chip
            icon={<FlagOutlinedIcon sx={{ fontSize: "13px !important", color: `${priorityColor} !important` }} />}
            label={priority}
            size="small"
            sx={{
              bgcolor: `${priorityColor}14`,
              color: priorityColor,
              fontWeight: 700,
              fontSize: 11,
              height: 22,
            }}
          />
          <Chip
            label={labelForStatus(status)}
            size="small"
            icon={<Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: `${statusStyle.dot} !important`, ml: "8px !important" }} />}
            sx={{
              bgcolor: statusStyle.bg,
              color: statusStyle.color,
              fontWeight: 700,
              fontSize: 11,
              height: 22,
              "& .MuiChip-icon": { mr: "-4px" },
            }}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ borderTop: "1px solid #F3F4F6", mt: 2, pt: 1.75, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          {/* Assignees */}
          <Tooltip title={assigneeNames.join(", ")}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              {assignees.length === 0 ? (
                <Avatar sx={{ width: 24, height: 24, fontSize: 11, fontWeight: 700, bgcolor: "#D1D5DB" }}>?</Avatar>
              ) : (
                <Box sx={{ display: "flex" }}>
                  {assignees.slice(0, 3).map((u, i) => (
                    <Avatar
                      key={u.id || i}
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: colorForName(u.name),
                        border: "2px solid #fff",
                        ml: i === 0 ? 0 : -0.9,
                      }}
                    >
                      {initialsFor(u.name)}
                    </Avatar>
                  ))}
                  {assignees.length > 3 && (
                    <Avatar sx={{ width: 24, height: 24, fontSize: 10, fontWeight: 700, bgcolor: "#E5E7EB", color: "#4B5563", border: "2px solid #fff", ml: -0.9 }}>
                      +{assignees.length - 3}
                    </Avatar>
                  )}
                </Box>
              )}
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: assignees.length ? "#374151" : "#9CA3AF",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {assignees.length ? assigneeNames.join(", ") : "Unassigned"}
              </Typography>
            </Box>
          </Tooltip>

          {/* Deadline + budget */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexShrink: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, color: "#9CA3AF" }}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{formatDate(deadline) || "No date"}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", color: "#111827" }}>
              <CurrencyRupeeIcon sx={{ fontSize: 13 }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{formatBudget(budget)}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  );
});

export default BriefCard;
